/**
 * Real-time Updates Utility
 * Handles WebSocket connections and real-time data synchronization
 */

import { io, Socket } from 'socket.io-client';

class RealtimeManager {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners = new Map<string, Set<Function>>();
  private subscriptions = new Map<string, Set<string>>(); // room -> Set of callback IDs
  private isConnected = false;
  private connectionPromise: Promise<void> | null = null;

  /**
   * Get WebSocket server URL
   */
  private getWebSocketUrl(): string {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

    // For local dev without env, use Vite proxy (current origin)
    // Vite will proxy /hltestmanager/* to localhost:6050
    if (isLocalHost && (!apiBaseUrl || apiBaseUrl === '')) {
      return window.location.origin; // Use Vite dev server URL (localhost:3000)
    }

    // Socket.IO client expects an http/https base URL (it will upgrade to ws/wss internally).
    // If apiBaseUrl is empty, fall back to current origin (covers prod when env is missing)
    let base = apiBaseUrl && apiBaseUrl.trim().length > 0 ? apiBaseUrl : window.location.origin;

    // Strip trailing slash
    base = base.replace(/\/$/, '');

    // Strip /hltestmanager from the end since we'll add it back in the path
    base = base.replace(/\/hltestmanager$/, '');

    return base;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    // Return existing connection promise if already connecting
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        const url = this.getWebSocketUrl();
        const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';

        // Connect to the base URL with namespace
        // Path: where Socket.IO is mounted (/hltestmanager/socket.io)
        // Namespace: the Socket.IO namespace (/realtime)
        // Full URL: wss://host/hltestmanager/realtime/socket.io/
        const connectionUrl = `${url}/realtime`;
        
        // Socket.IO path - where the Socket.IO server is mounted
        const socketPath = '/hltestmanager/socket.io';
        
        console.log('🔌 Connecting WebSocket:', { 
          connectionUrl, 
          socketPath,
          isLocalHost,
          apiBaseUrl,
          fullUrl: `${connectionUrl}${socketPath}`
        });
        
        this.socket = io(connectionUrl, {
          path: socketPath,
          transports: ['websocket', 'polling'],
          reconnection: false, // we explicitly reconnect by calling connect() again
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          reconnectionDelayMax: 5000,
          timeout: 30000, // Increased timeout to 30 seconds
          autoConnect: false, // we will call socket.connect() manually
          forceNew: true, // Do not reuse cached managers; ensures full teardown on disconnect
        });

        // Manually start the connection
        this.socket.connect();

        this.socket.on('connect', () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          console.log('✅ WebSocket connected');
          this.emit('connected');
          
          // Re-subscribe to all previous subscriptions
          this.subscriptions.forEach((callbackIds, room) => {
            const [entityType, entityId] = room.split(':');
            this.socket?.emit('subscribe', { entityType, entityId });
          });
          
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          this.isConnected = false;
          console.log('❌ WebSocket disconnected:', reason);
          
          if (reason === 'io server disconnect') {
            // Server disconnected, try to reconnect
            this.socket?.connect();
          }
        });

        this.socket.on('connect_error', (error) => {
          console.error('WebSocket connection error:', error);
          this.reconnectAttempts++;
          
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('❌ Max reconnection attempts reached');
            this.connectionPromise = null;
            reject(error);
          }
        });

        this.socket.on('update', (data: any) => {
          const { entityType, entityId, action, data: updateData } = data;
          const key = `${entityType}:${entityId}`;
          
          // Notify all listeners for this entity
          const listeners = this.listeners.get(key);
          if (listeners) {
            listeners.forEach(callback => {
              try {
                callback({
                  action,
                  data: updateData,
                  timestamp: data.timestamp,
                });
              } catch (error) {
                console.error('Error in real-time callback:', error);
              }
            });
          }
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Re-subscribe to all previous subscriptions
          this.subscriptions.forEach((callbackIds, room) => {
            const [entityType, entityId] = room.split(':');
            this.socket?.emit('subscribe', { entityType, entityId });
          });
        });
      
    } catch (error) {
        console.error('WebSocket connection failed:', error);
        this.connectionPromise = null;
        reject(error);
    }
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.socket) {
      const s = this.socket;

      // Stop Socket.IO from reconnecting
      try {
        s.io.opts.reconnection = false;
      } catch {}

      // 1. Disconnect namespace
      s.disconnect();

      // 2. Close the underlying Engine.IO transport explicitly
      try {
        const engine: any = s.io.engine;
        engine?.close?.(); // closes engine + transport + ws
      } catch (err) {
        console.warn('Engine close failed:', err);
      }

      // 3. Destroy the manager completely
      try {
        (s.io as any).close?.(); // Socket.IO Manager close
      } catch {}

      this.socket = null;
    }
    this.isConnected = false;
    this.connectionPromise = null;
    this.listeners.clear();
    this.subscriptions.clear();
    
    console.log('🔌 WebSocket FULLY terminated.');
  }

  /**
   * Subscribe to real-time updates for a specific entity
   */
  async subscribe(entityType: string, entityId: string, callback: Function): Promise<() => void> {
    const key = `${entityType}:${entityId}`;
    
    // Ensure connection - wait for it to be established
    if (!this.isConnected) {
      if (!this.connectionPromise) {
        this.connectionPromise = this.connect();
      }
      try {
        await this.connectionPromise;
      } catch (error) {
        console.warn('WebSocket connection failed, subscription may not work:', error);
        // Continue anyway - subscription will be retried on reconnect
      }
    }
    
    // Add listener
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(callback);
    
    // Track subscription
    if (!this.subscriptions.has(key)) {
      this.subscriptions.set(key, new Set());
    }
    const callbackId = `${Date.now()}_${Math.random()}`;
    this.subscriptions.get(key)!.add(callbackId);
    
    // Subscribe on server (retry if not connected yet)
    if (this.socket && this.isConnected) {
      this.socket.emit('subscribe', { entityType, entityId });
    } else {
      // If not connected yet, wait a bit and retry
      setTimeout(() => {
        if (this.socket && this.isConnected) {
          this.socket.emit('subscribe', { entityType, entityId });
        }
      }, 500);
    }
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(key);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.listeners.delete(key);
        }
      }
      
      // Unsubscribe from server if no more listeners
      const subs = this.subscriptions.get(key);
      if (subs) {
        subs.delete(callbackId);
        if (subs.size === 0) {
          this.subscriptions.delete(key);
          if (this.socket && this.isConnected) {
            this.socket.emit('unsubscribe', { entityType, entityId });
          }
        }
      }

      // If no listeners and no subscriptions remain anywhere, fully disconnect
      if (this.listeners.size === 0 && this.subscriptions.size === 0) {
        console.log('[Realtime] All subscriptions removed → Closing WebSocket');
        this.disconnect();
      }
    };
  }

  /**
   * Emit event to all listeners (local only, for backward compatibility)
   */
  emit(event: string, data: any = null) {
    if (event === 'connected') {
      return;
    }

    // Find relevant listeners
    const [entityType, entityId] = event.split(':');
    const key = `${entityType}:${entityId}`;
    const listeners = this.listeners.get(key);
    
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in real-time callback:', error);
        }
      });
    }
  }

  /**
   * Send message to server
   */
  send(message: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit('message', message);
    } else {
      console.warn('WebSocket not connected, message not sent:', message);
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      listeners: this.listeners.size,
      subscriptions: this.subscriptions.size,
    };
  }

  /**
   * Debug helper for devtools
   */
  getDebugState() {
    return {
      connected: this.isConnected,
      listeners: this.listeners.size,
      subscriptions: this.subscriptions.size,
      socketState: this.socket ? (this.socket.io as any)?.engine?.readyState : 'none'
    };
  }
}

// Create singleton instance
const realtimeManager = new RealtimeManager();

// Expose lightweight debug helpers (always, but harmless in prod)
if (typeof window !== 'undefined') {
  (window as any).__realtimeStatus = () => realtimeManager.getStatus();
  (window as any).__realtimeDisconnect = () => realtimeManager.disconnect();
  (window as any).__realtimeState = () => realtimeManager.getDebugState();
}

// DO NOT auto-connect on module load
// WebSocket will only connect when explicitly needed (e.g., on test run page)
// This reduces unnecessary connections and improves performance

export default realtimeManager;

/**
 * Vue composable for real-time updates
 */
export function useRealtime() {
  return {
    connect: () => realtimeManager.connect(),
    disconnect: () => realtimeManager.disconnect(),
    subscribe: (entityType: string, entityId: string, callback: Function) => 
      realtimeManager.subscribe(entityType, entityId, callback),
    send: (message: any) => realtimeManager.send(message),
    isConnected: () => realtimeManager.getStatus().connected,
    getStatus: () => realtimeManager.getStatus(),
  };
}

/**
 * Real-time data synchronization helpers
 */
export const realtimeHelpers = {
  /**
   * Subscribe to bug updates
   */
  subscribeToBug(bugId: string, callback: Function) {
    return realtimeManager.subscribe('bug', bugId, callback);
  },

  /**
   * Subscribe to test case updates
   */
  subscribeToTestCase(testCaseId: string, callback: Function) {
    return realtimeManager.subscribe('test_case', testCaseId, callback);
  },

  /**
   * Subscribe to test run updates
   */
  subscribeToTestRun(testRunId: string, callback: Function) {
    return realtimeManager.subscribe('test_run', testRunId, callback);
  },

  /**
   * Subscribe to project updates
   */
  subscribeToProject(projectId: string, callback: Function) {
    return realtimeManager.subscribe('project', projectId, callback);
  },

  /**
   * Subscribe to team updates
   */
  subscribeToTeam(teamId: string, callback: Function) {
    return realtimeManager.subscribe('team', teamId, callback);
  },

  /**
   * Notify about data changes (local only, for backward compatibility)
   */
  notifyChange(entityType: string, entityId: string, action: string, data: any) {
    realtimeManager.emit(`${entityType}:${entityId}`, {
      action,
      data,
      timestamp: new Date(),
    });
  },
};
